import { Activity, ArrowDown, ArrowUp, CalendarDays, Clock, Droplets, Info, Layers3, Package, PackageCheck, Repeat, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getLiveTracker } from '@/api/member';
import { fmtUsd, fmtUsdAmount } from '@/lib/money';

/* ---------- Smooth animated counter (cubic ease-out) ---------- */
function useSmoothNumber(target, ms = 1100) {
  const [val, setVal] = useState(target ?? 0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(val);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `val` is only the tween start point, read when target/ms change; depending on it would restart the tween every frame
  useEffect(() => {
    if (target == null) return undefined;
    if (val === target) return undefined;
    cancelAnimationFrame(rafRef.current);
    fromRef.current = val;
    startRef.current = null;
    const ease = (t) => 1 - (1 - t) ** 3;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const p = Math.min(1, (ts - startRef.current) / ms);
      setVal(fromRef.current + (target - fromRef.current) * ease(p));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, ms]);
  return val;
}

/* ---------- Patch v17.1 — formatting helpers ----------
 * fmtFull   → full thousands-separator number (e.g. 12,543). Replaces
 *             the old fmtCompact for the 4 main Stat cards and the
 *             new Cadence row so abbreviations like "12.5K" never
 *             hide the actual numeric scale.
 * fmtMoney  → currency variant for the Volume Distributed card.
 * fmtCompact remains in the file as a defensive fallback for any
 *             pathological mega-scale (>=1B) so layout doesn't break,
 *             but the cards intentionally call fmtFull first.
 */
function fmtFull(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const num = Number(n);
  if (Math.abs(num) >= 1e12) return num.toExponential(2);
  return Math.round(num).toLocaleString('en-US');
}

function fmtMoney(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const num = Number(n);
  if (Math.abs(num) >= 1e12) return `$${num.toExponential(2)}`;
  return fmtUsd(num);
}

function fmtEstimate(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const num = Number(n);
  if (num >= 100) return Math.round(num).toLocaleString('en-US');
  return num.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

const Pulse = () => (
  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#FFC20E]">
    <span className="w-1.5 h-1.5 rounded-full bg-[#FFB612] animate-pulse" />
    Live
  </span>
);

/* ---------- Patch v17.1 — Heartbeat countdown pill ----------
 * Counts down to the next scheduled cycle tick. Re-anchors every poll
 * response (next_tick_at). If the next-tick moment passes before the
 * next poll lands, we display "Tick due now…" so users never see a
 * negative number.
 */
function useCountdown(nextTickIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!nextTickIso) return null;
  const tick = new Date(nextTickIso).getTime();
  if (Number.isNaN(tick)) return null;
  const remaining = Math.max(0, Math.ceil((tick - now) / 1000));
  return remaining;
}

const HeartbeatPill = ({ nextTickIso, intervalSeconds }) => {
  const remaining = useCountdown(nextTickIso);
  if (intervalSeconds == null) return null;
  const label = remaining == null ? 'Awaiting first tick…' : remaining <= 0 ? 'Tick due now…' : `Next cycle tick in ${formatSeconds(remaining)}`;
  return (
    <div className="inline-flex items-center gap-2 sc-glass rounded-full px-3 py-1.5" data-testid="heartbeat-countdown-pill">
      <Clock className="w-3.5 h-3.5 text-[#FFC20E]" />
      <span className="sc-tabular text-[11px] tracking-[0.18em] uppercase text-[var(--sc-text-dim)]" data-testid="heartbeat-countdown-label">
        {label}
      </span>
      <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">· every {intervalSeconds}s</span>
    </div>
  );
};

function formatSeconds(s) {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

const LiveTracker = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    let timer = null;
    const tick = async () => {
      try {
        const d = await getLiveTracker();
        if (alive) setData(d);
        setError(null);
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load live data');
      } finally {
        // Slow-poll (2025-06): once-per-minute is sufficient — cycle
        // engine ticks at 60s anyway. F5 always gives instant fresh data.
        if (alive) timer = setTimeout(tick, 60000);
      }
    };
    tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const totalVolume = useSmoothNumber(data?.pool?.total_volume_distributed_usd ?? 0);
  const fillers = useSmoothNumber(data?.pool?.active_rotator_pool_size_fillers ?? 0);
  const roots = useSmoothNumber(data?.pool?.active_rotator_pool_size_roots ?? 0);
  const cycles = useSmoothNumber(data?.pool?.total_cycles_completed ?? 0);

  // Patch v17.1 — Sub Pack Activity (top transparency strip)
  const subpackActive = useSmoothNumber(data?.subpack_activity?.active_count ?? 0);
  const subpackYearly = useSmoothNumber(data?.subpack_activity?.active_yearly_count ?? 0);
  const subpackSpeedy = useSmoothNumber(data?.subpack_activity?.active_speedy_count ?? 0);
  const subpackExpired = useSmoothNumber(data?.subpack_activity?.expired_count ?? 0);
  const subpackDrips = useSmoothNumber(data?.subpack_activity?.estimated_drips_per_hour ?? 0);

  // Patch v17.1 — Cycle Cadence (real-calendar buckets, UTC)
  const cycToday = useSmoothNumber(data?.cycles_cadence?.today ?? 0);
  const cycWeek = useSmoothNumber(data?.cycles_cadence?.this_week ?? 0);
  const cycMonth = useSmoothNumber(data?.cycles_cadence?.this_month ?? 0);
  const cycYear = useSmoothNumber(data?.cycles_cadence?.this_year ?? 0);

  return (
    <section className="sc-glass-strong rounded-2xl p-5 md:p-7 relative overflow-hidden" data-testid="live-tracker">
      <div
        className="absolute -top-24 -right-16 w-72 h-72 pointer-events-none rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
        }}
      />
      <div className="relative z-10 flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="sc-pill">
            <span className="sc-dot" /> Real-Time Pool Activity
          </span>
        </div>
        <Pulse />
      </div>

      {/* ---------- v17.1 #1 — Heartbeat countdown ---------- */}
      <div className="relative z-10 mb-4">
        <HeartbeatPill nextTickIso={data?.next_tick_at} intervalSeconds={data?.heartbeat_interval_seconds} />
      </div>

      {/* ---------- v17.1 #2 — Sub Pack Activity (top strip) ---------- */}
      <div className="relative z-10 sc-glass rounded-xl p-4 mb-5" data-testid="subpack-activity-strip">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Sub Pack Activity · Platform-Wide</span>
          <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">transparency strip</span>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <MiniStat icon={Package} label="Active Yearly Sub Packs" value={fmtFull(subpackYearly)} testid="subpack-yearly" />
          <MiniStat icon={Zap} label="Active Speedy Packs" value={fmtFull(subpackSpeedy)} testid="subpack-speedy" />
          <MiniStat icon={Layers3} label="Total Active Sub Packs" value={fmtFull(subpackActive)} testid="subpack-active" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <MiniStat icon={PackageCheck} label="Expired Sub Packs" value={fmtFull(subpackExpired)} testid="subpack-expired" />
          <MiniStat icon={Droplets} label="Est. Drips / Hour" value={`~${fmtEstimate(subpackDrips)}`} sublabel="estimated" testid="subpack-drips" />
        </div>
      </div>

      {/* ---------- Existing 4 stat cards (full-number format now) ---------- */}
      <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
        <Stat icon={Activity} label="Total Volume Distributed" value={fmtMoney(totalVolume)} accent="USD" testid="stat-volume" />
        <Stat icon={Layers3} label="Active Filler Pool" value={fmtFull(fillers)} accent="positions" testid="stat-filler" />
        <Stat icon={Repeat} label="Active Root Pool" value={fmtFull(roots)} accent="positions" testid="stat-root" />
        <Stat icon={Zap} label="Cycles Completed" value={fmtFull(cycles)} accent="all-time" testid="stat-cycles" />
      </div>

      {/* ---------- v17.1 #4 — Cycle Cadence row ---------- */}
      <div className="relative z-10 sc-glass rounded-xl p-4 mt-5" data-testid="cycles-cadence-strip">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-3.5 h-3.5 text-[#FFC20E]" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Cycle Cadence · Real Calendar (UTC)</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Today" value={fmtFull(cycToday)} testid="cadence-today" />
          <MiniStat label="This Week" value={fmtFull(cycWeek)} testid="cadence-week" />
          <MiniStat label="This Month" value={fmtFull(cycMonth)} testid="cadence-month" />
          <MiniStat label="This Year" value={fmtFull(cycYear)} testid="cadence-year" />
        </div>
      </div>

      {/* ---------- v17.1 — Stacked Going OUT / Going IN panels ---------- */}
      <div className="relative z-10 mt-6 grid grid-cols-1 gap-5">
        <RecentGlobalCyclesList
          title="Going Out · Recent Cycles"
          subtitle="Positions cycled (last 25)"
          icon={ArrowUp}
          rows={(data?.recent_global_cycles || []).slice(0, 25)}
          empty="Awaiting first cycle…"
        />
        <PoolInflowsList
          title="Going In · Pool Inflows"
          subtitle="Positions entering the filler pool (last 25)"
          icon={ArrowDown}
          rows={(data?.recent_pool_inflows || []).slice(0, 25)}
          empty="No recent inflows…"
        />
        <RecentList
          title="Your Recent Ledger"
          rows={(data?.my_recent_ledger || []).slice(0, 6)}
          empty="No activity yet— secure your first position to start."
          render={(r) => ({
            left: `${r.type} · ${Number(r.amount).toFixed(2)}`,
            right: shortTs(r.timestamp),
          })}
        />
      </div>

      {error && (
        <div className="relative z-10 mt-3 text-xs text-[#ff8a8a]" data-testid="live-tracker-error">
          {error}
        </div>
      )}
    </section>
  );
};

/* ---------- Patch v17.4 — Adaptive font-size tier ladder ----------
 *
 * Pre-v17.4 the Live Network's 4 primary stat cards rendered the
 * number at a fixed ``text-2xl`` (24px) with ``break-all``. After the
 * v17.1 switch from abbreviated ("12.5K") to full-number ("12,547")
 * formatting, longer numeric strings would wrap onto a second line and
 * force the cards to grow vertically — clumping the layout once values
 * crossed into the tens of thousands.
 *
 * This helper returns a Tailwind text-size class chosen from the
 * rendered string's length so the number always stays on one line
 * inside the card without overflowing. ``break-all`` is also swapped
 * for ``whitespace-nowrap`` so numbers never break mid-digit.
 *
 * Ladder:
 *   ≤ 5 chars  → text-2xl   (24px — original size for "$0.00", "1,234")
 *   ≤ 7 chars  → text-xl    (20px — "$12,345", "100,000")
 *   ≤ 10 chars → text-lg    (18px — "$1,234,567", "12,345,678")
 *   ≤ 13 chars → text-base  (16px — "$99,999,999", "1,000,000,000")
 *   > 13 chars → text-sm    (14px — billions+, scientific notation)
 */
const statValueSizeClass = (value) => {
  const len = String(value ?? '').length;
  if (len <= 5) return 'text-2xl';
  if (len <= 7) return 'text-xl';
  if (len <= 10) return 'text-lg';
  if (len <= 13) return 'text-base';
  return 'text-sm';
};

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="sc-glass rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center border"
        style={{
          background: 'rgba(255,182,18,0.06)',
          borderColor: 'rgba(255,182,18,0.28)',
        }}
      >
        <Icon className="w-4 h-4 text-[#FFC20E]" />
      </div>
      <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{accent}</span>
    </div>
    <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mt-3">{label}</div>
    <div
      className={`sc-tabular font-display font-bold ${statValueSizeClass(value)} mt-1 leading-none sc-gold-text whitespace-nowrap overflow-hidden text-ellipsis`}
      data-testid={testid ? `${testid}-value` : undefined}
    >
      {value}
    </div>
  </div>
);

/* ---------- v17.1 — Compact mini-stat (used by Sub Pack + Cadence rows) ---------- */
const MiniStat = ({ icon: Icon, label, value, sublabel, testid }) => (
  <div className="rounded-lg px-3 py-2.5 border border-[rgba(255,182,18,0.10)] bg-[rgba(0,0,0,0.20)]" data-testid={testid}>
    <div className="flex items-center gap-1.5">
      {Icon ? <Icon className="w-3 h-3 text-[#FFC20E]" /> : null}
      <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{label}</span>
    </div>
    <div className="sc-tabular font-display font-bold text-xl mt-1 leading-none sc-gold-text break-all" data-testid={testid ? `${testid}-value` : undefined}>
      {value}
    </div>
    {sublabel ? <div className="text-[8px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mt-1">{sublabel}</div> : null}
  </div>
);

const RecentList = ({ title, rows, empty, render }) => (
  <div className="sc-glass rounded-xl p-4">
    <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-2">{title}</div>
    {rows.length === 0 ? (
      <div className="py-6 text-center text-[12px] text-[var(--sc-text-mute)]">{empty}</div>
    ) : (
      <ul className="divide-y divide-[rgba(255,182,18,0.06)]">
        {rows.map((r, i) => {
          const { left, right } = render(r);
          return (
            <li key={i} className="py-1.5 flex items-center justify-between gap-2 text-[12px]">
              <span className="truncate text-[var(--sc-text-dim)]">{left}</span>
              <span className="sc-tabular text-[var(--sc-text-mute)] text-[10px]">{right}</span>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

function shortTs(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/* ---------- Recent Global Cycles list (Going Out) ----------
 * Patch v17.1 — moved into a full-width stacked panel (no longer
 * shares a 2-col row with the Ledger). Header now includes an icon
 * + subtitle for clearer visual hierarchy. Underlying row template
 * (cycler · amount / cycled / purchased) is UNCHANGED from v16 to
 * preserve the randomness-transparency display contract.
 */
const RecentGlobalCyclesList = ({ title, subtitle, icon: Icon, rows, empty }) => (
  <div className="sc-glass rounded-xl p-4" data-testid="recent-global-cycles">
    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
      <div className="flex items-center gap-2">
        {Icon ? <Icon className="w-3.5 h-3.5 text-[#FFC20E]" /> : null}
        <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{title}</span>
      </div>
      {subtitle ? <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{subtitle}</span> : null}
    </div>
    {rows.length === 0 ? (
      <div className="py-6 text-center text-[12px] text-[var(--sc-text-mute)]">{empty}</div>
    ) : (
      <>
        {/* Column header row */}
        <div
          className="grid grid-cols-[1fr_auto_auto] gap-3 items-center pb-1.5 mb-1 border-b border-[rgba(255,182,18,0.08)]"
          data-testid="recent-global-cycles-header"
        >
          <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Cycler · Amount</span>
          <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] text-right">Cycled</span>
          <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] text-right pl-3">Purchased</span>
        </div>
        <ul className="divide-y divide-[rgba(255,182,18,0.06)]">
          {rows.map((r, i) => {
            const amt = fmtUsdAmount(r.amount_display ?? r.amount);
            const label = r.label || 'cycle payout';
            const who = r.recipient_username ? `@${r.recipient_username} · ` : '';
            return (
              <li key={i} className="py-1.5 grid grid-cols-[1fr_auto_auto] gap-3 items-center text-[12px]" data-testid={`recent-global-cycle-row-${i}`}>
                <span className="truncate text-[var(--sc-text-dim)]">
                  {who}+${amt} · {label}
                </span>
                <span className="sc-tabular text-[var(--sc-text-mute)] text-[10px] text-right" data-testid={`recent-global-cycle-time-${i}`}>
                  {shortTs(r.timestamp)}
                </span>
                <span
                  className="sc-tabular text-[var(--sc-text-mute)] text-[10px] text-right pl-3"
                  data-testid={`recent-global-cycle-purchase-${i}`}
                  title={r.purchase_timestamp || 'no archive record'}
                >
                  {r.purchase_timestamp ? shortTs(r.purchase_timestamp) : '—'}
                </span>
              </li>
            );
          })}
        </ul>
      </>
    )}
  </div>
);

/* ---------- Patch v17.1 — Pool Inflows list (Going In) ----------
 * Three event types, all sourced from existing LEDGER rows. No
 * coloured badges or icons — count column alone is the sanity check
 * (+1 / +2 / +5 uniquely identifies each event type). Minimal
 * styling keeps the list dense and mobile-friendly.
 *
 * Explainer note pinned beneath the list directs members to the
 * Sub Pack Activity strip near the top of the page for drip
 * transparency, since drip events are intentionally not in this
 * list.
 */
const POOL_INFLOW_LABELS = {
  single: 'Single',
  snowball: 'Legacy Snowball',
  rebuy: 'Legacy Snowball',
  auto_rebuy: 'Auto-Rebuy',
  subpack: 'Sub Pack',
};

const PoolInflowsList = ({ title, subtitle, icon: Icon, rows, empty }) => (
  <div className="sc-glass rounded-xl p-4" data-testid="recent-pool-inflows">
    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
      <div className="flex items-center gap-2">
        {Icon ? <Icon className="w-3.5 h-3.5 text-[#FFC20E]" /> : null}
        <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{title}</span>
      </div>
      {subtitle ? <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{subtitle}</span> : null}
    </div>
    {rows.length === 0 ? (
      <div className="py-6 text-center text-[12px] text-[var(--sc-text-mute)]">{empty}</div>
    ) : (
      <>
        {/* Column header row */}
        <div
          className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center pb-1.5 mb-1 border-b border-[rgba(255,182,18,0.08)]"
          data-testid="recent-pool-inflows-header"
        >
          <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">User</span>
          <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] text-right">Type</span>
          <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] text-right pl-2">Positions</span>
          <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] text-right pl-3">Time</span>
        </div>
        <ul className="divide-y divide-[rgba(255,182,18,0.06)]">
          {rows.map((r, i) => {
            const typeLabel = POOL_INFLOW_LABELS[r.event_type] || r.event_type || '—';
            const who = r.username ? `@${r.username}` : '@unknown';
            return (
              <li key={i} className="py-1.5 grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center text-[12px]" data-testid={`recent-pool-inflow-row-${i}`}>
                <span className="truncate text-[var(--sc-text-dim)]">{who}</span>
                <span className="sc-tabular text-[var(--sc-text-mute)] text-[11px] text-right" data-testid={`recent-pool-inflow-type-${i}`}>
                  {typeLabel}
                </span>
                <span className="sc-tabular text-[var(--sc-text-dim)] text-[11px] text-right pl-2" data-testid={`recent-pool-inflow-count-${i}`}>
                  +{r.count}
                </span>
                <span className="sc-tabular text-[var(--sc-text-mute)] text-[10px] text-right pl-3" data-testid={`recent-pool-inflow-time-${i}`}>
                  {shortTs(r.timestamp)}
                </span>
              </li>
            );
          })}
        </ul>
      </>
    )}
    {/* Explainer note — pinned to bottom of the panel */}
    <div
      className="mt-3 pt-3 border-t border-[rgba(255,182,18,0.08)] flex items-start gap-2 text-[11px] leading-relaxed text-[var(--sc-text-mute)]"
      data-testid="pool-inflows-explainer"
    >
      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#FFC20E]" />
      <span>
        This feed shows manual <b>Single Position</b> purchases, <b>Sub Pack</b> drip releases (+1 per drip), and historical <b>Legacy Snowball</b> auto-rebuys
        (disabled in v17.5 — the full $3.00 per cycle now distributes to real accounts with zero reinvestment).
      </span>
    </div>
  </div>
);

export default LiveTracker;
